/*!
 * audio-output-picker.js — shared "choose the audio OUTPUT device" control.
 *
 * WHY THIS EXISTS
 * ---------------
 * The same 25-line block was copy-pasted into /speaker-test, /white-noise and
 * /connection-keeper, and it had the same three defects in all three copies:
 *
 *   1. When enumerateDevices() came back without labels it SILENTLY called
 *      `new MicManager().start()` — i.e. a playback-only tool popped a
 *      microphone permission prompt. Measured on /speaker-test over 14 days:
 *      1399 sessions requested the mic, 646 of them hit a failure dialog
 *      (253 dismissed, 213 "microphone not found", 119 denied, …). The copy
 *      users saw was MicManager's recording copy — "Allow microphone access,
 *      without it we cannot record" / "Microphone not available in this
 *      browser … to record audio, open timbrica.com in Chrome" — on a tool
 *      that records nothing. Those failures also polluted the tool's
 *      `tool_blocked` telemetry with mic reasons.
 *   2. The control was offered even where output switching is impossible.
 *      Measured (headed Playwright, real hardware, 2026-07-30):
 *        Chromium  AudioContext.setSinkId ✔
 *        Firefox   AudioContext.setSinkId ✘   (selectAudioOutput ✔, unusable
 *                                              for a Web Audio graph)
 *        WebKit    navigator.mediaDevices  ✘
 *        Chromium's Speaker Selection API: "not enabled"
 *      So on every non-Chromium engine and on all of mobile the whole flow
 *      was: ask for the microphone → fail → show nothing usable.
 *   3. The empty-result path still latched `devicesDetected = true`, hiding the
 *      trigger button for good and leaving a <select> with one dead
 *      "Default" entry. (9 rage-clicks on the refresh button in 14 days.)
 *
 * WHAT THIS DOES INSTEAD
 * ----------------------
 *   - Capability gate first: no `AudioContext.prototype.setSinkId` → the whole
 *     control is removed from the DOM flow and the microphone is never touched.
 *   - Labels present already (permission granted earlier in this origin) →
 *     populate, no prompt, ever.
 *   - Labels missing → NO silent escalation. An honest inline note explains
 *     that the browser only reveals output *names* after a microphone grant and
 *     that this tool does not record, plus an explicit opt-in button. Same
 *     shape as the pattern /bluetooth-test already shipped.
 *   - No microphone hardware at all (no audioinput of any kind) → the opt-in is
 *     not offered; an inline note says output names are unavailable. This is
 *     the 213 "microphone not found" dialogs.
 *   - Failures render an inline note. Never a modal, never `tool_blocked`.
 *   - Emits `feature_use{f:'output_device_selected'}` so "does anyone actually
 *     switch the output?" becomes answerable — it never was.
 *
 * USAGE
 *   var picker = window.AudioOutputPicker.attach({
 *       wrap: '#stDeviceGroup', detect: '#stDeviceDetect',
 *       select: '#stDeviceSelect', refresh: '#stDeviceRefresh',
 *       slug: 'speaker-test',
 *       strings: { default: '…', unknown: '…' },   // from the tool's lang file
 *       apply: function (deviceId) { ... }          // route your audio
 *   });
 *   picker.supported        // false => control hidden, nothing else will fire
 *   picker.selectedId()     // '' when the system default is in use
 */
(function () {
    'use strict';

    /* Only the strings that are NEW to this control live here; `default` /
       `unknown` come from the calling tool's own lang file so existing
       translations stay authoritative. */
    var COPY = {
        en: {
            why: 'Browsers only reveal output device <em>names</em> after a microphone permission. This tool does not record anything — the microphone is used for nothing else.',
            grant: 'Show device names',
            denied: 'Without permission the browser hides device names. Sound goes to the output device chosen in your system.',
            nomic: 'This device has no microphone, so the browser will not reveal output device names. Sound goes to the output device chosen in your system.',
            failed: 'Could not read the device list. Sound goes to the output device chosen in your system.',
        },
        ru: {
            why: 'Названия устройств вывода браузер отдаёт только после разрешения на микрофон. Этот инструмент ничего не записывает — микрофон больше ни для чего не нужен.',
            grant: 'Показать названия устройств',
            denied: 'Без разрешения браузер скрывает названия устройств. Звук идёт в то устройство вывода, которое выбрано в системе.',
            nomic: 'На этом устройстве нет микрофона, поэтому браузер не отдаёт названия выходов. Звук идёт в то устройство вывода, которое выбрано в системе.',
            failed: 'Не удалось получить список устройств. Звук идёт в то устройство вывода, которое выбрано в системе.',
        },
        ko: {
            why: '브라우저는 마이크 권한을 허용한 뒤에만 출력 장치 <em>이름</em>을 알려줍니다. 이 도구는 아무것도 녹음하지 않으며, 마이크는 다른 용도로 쓰이지 않습니다.',
            grant: '장치 이름 표시',
            denied: '권한이 없으면 브라우저가 장치 이름을 숨깁니다. 소리는 시스템에서 선택한 출력 장치로 나갑니다.',
            nomic: '이 기기에는 마이크가 없어 브라우저가 출력 장치 이름을 알려주지 않습니다. 소리는 시스템에서 선택한 출력 장치로 나갑니다.',
            failed: '장치 목록을 읽을 수 없습니다. 소리는 시스템에서 선택한 출력 장치로 나갑니다.',
        },
        ar: {
            why: 'لا تكشف المتصفحات <em>أسماء</em> أجهزة الإخراج إلا بعد الإذن بالوصول إلى الميكروفون. هذه الأداة لا تسجّل أي شيء، والميكروفون لا يُستخدم لأي غرض آخر.',
            grant: 'إظهار أسماء الأجهزة',
            denied: 'بدون إذن يخفي المتصفح أسماء الأجهزة. يخرج الصوت من جهاز الإخراج المحدد في نظامك.',
            nomic: 'لا يوجد ميكروفون في هذا الجهاز، لذلك لن يكشف المتصفح أسماء أجهزة الإخراج. يخرج الصوت من جهاز الإخراج المحدد في نظامك.',
            failed: 'تعذّر قراءة قائمة الأجهزة. يخرج الصوت من جهاز الإخراج المحدد في نظامك.',
        },
        id: {
            why: 'Browser hanya menampilkan <em>nama</em> perangkat keluaran setelah izin mikrofon diberikan. Alat ini tidak merekam apa pun — mikrofon tidak dipakai untuk hal lain.',
            grant: 'Tampilkan nama perangkat',
            denied: 'Tanpa izin, browser menyembunyikan nama perangkat. Suara keluar melalui perangkat yang dipilih di sistem Anda.',
            nomic: 'Perangkat ini tidak punya mikrofon, jadi browser tidak akan menampilkan nama perangkat keluaran. Suara keluar melalui perangkat yang dipilih di sistem Anda.',
            failed: 'Tidak dapat membaca daftar perangkat. Suara keluar melalui perangkat yang dipilih di sistem Anda.',
        },
        pt: {
            why: 'Os navegadores só revelam os <em>nomes</em> dos dispositivos de saída depois da permissão de microfone. Esta ferramenta não grava nada — o microfone não é usado para mais nada.',
            grant: 'Mostrar nomes dos dispositivos',
            denied: 'Sem permissão o navegador oculta os nomes dos dispositivos. O som vai para o dispositivo de saída escolhido no seu sistema.',
            nomic: 'Este dispositivo não tem microfone, por isso o navegador não revela os nomes das saídas. O som vai para o dispositivo de saída escolhido no seu sistema.',
            failed: 'Não foi possível ler a lista de dispositivos. O som vai para o dispositivo de saída escolhido no seu sistema.',
        },
        de: {
            why: 'Browser geben die <em>Namen</em> der Ausgabegeräte erst nach einer Mikrofon-Berechtigung heraus. Dieses Werkzeug nimmt nichts auf — das Mikrofon wird für nichts anderes verwendet.',
            grant: 'Gerätenamen anzeigen',
            denied: 'Ohne Berechtigung verbirgt der Browser die Gerätenamen. Der Ton geht an das im System gewählte Ausgabegerät.',
            nomic: 'Dieses Gerät hat kein Mikrofon, daher gibt der Browser die Namen der Ausgänge nicht heraus. Der Ton geht an das im System gewählte Ausgabegerät.',
            failed: 'Die Geräteliste konnte nicht gelesen werden. Der Ton geht an das im System gewählte Ausgabegerät.',
        },
        es: {
            why: 'Los navegadores solo revelan los <em>nombres</em> de los dispositivos de salida tras conceder permiso de micrófono. Esta herramienta no graba nada: el micrófono no se usa para nada más.',
            grant: 'Mostrar nombres de dispositivos',
            denied: 'Sin permiso el navegador oculta los nombres de los dispositivos. El sonido va al dispositivo de salida elegido en tu sistema.',
            nomic: 'Este dispositivo no tiene micrófono, así que el navegador no revelará los nombres de las salidas. El sonido va al dispositivo de salida elegido en tu sistema.',
            failed: 'No se pudo leer la lista de dispositivos. El sonido va al dispositivo de salida elegido en tu sistema.',
        },
        fr: {
            why: 'Les navigateurs ne révèlent les <em>noms</em> des périphériques de sortie qu’après une autorisation du microphone. Cet outil n’enregistre rien — le microphone ne sert à rien d’autre.',
            grant: 'Afficher les noms des périphériques',
            denied: 'Sans autorisation, le navigateur masque les noms des périphériques. Le son part vers le périphérique de sortie choisi dans votre système.',
            nomic: 'Cet appareil n’a pas de microphone, le navigateur ne révélera donc pas les noms des sorties. Le son part vers le périphérique de sortie choisi dans votre système.',
            failed: 'Impossible de lire la liste des périphériques. Le son part vers le périphérique de sortie choisi dans votre système.',
        },
        uk: {
            why: 'Браузери показують <em>назви</em> пристроїв виводу лише після дозволу на мікрофон. Цей інструмент просить дозвіл саме для цього.',
            grant: 'Показати назви пристроїв',
            denied: 'Без дозволу браузер приховує назви пристроїв. Звук піде на пристрій виводу, вибраний у системі.',
            nomic: 'На цьому пристрої немає мікрофона, тому браузер не покаже назви виводів. Звук піде на пристрій, вибраний у системі.',
            failed: 'Не вдалося прочитати список пристроїв. Звук піде на пристрій виводу, вибраний у системі.',
        }
    };

    var BT_RE = /bluetooth|\bbt\b|airpods|galaxy.?buds|bose|jbl|sony.?w[hf]|beats|jabra|pixel.?buds/i;
    var HDMI_RE = /hdmi|displayport|\btv\b|receiver|soundbar|sonos/i;
    var HP_RE = /headphone|headset|earphone|earbud|наушник|наушники/i;

    function el(x) { return typeof x === 'string' ? document.querySelector(x) : (x || null); }
    function show(e) { if (e) e.classList.remove('hidden'); }
    function hide(e) { if (e) e.classList.add('hidden'); }

    function iconFor(label) {
        if (BT_RE.test(label)) return '📶';
        if (HDMI_RE.test(label)) return '📺';
        if (HP_RE.test(label)) return '🎧';
        return '🔊';
    }

    /* Output routing is only possible through AudioContext.setSinkId. Firefox
       has selectAudioOutput() but no AudioContext.setSinkId, and routing a Web
       Audio graph through MediaStreamAudioDestination -> <audio>.setSinkId to
       reach it costs a second output path for ~1.5% of blocked sessions; not
       worth it. WebKit has no mediaDevices at all in the builds we measured. */
    function isSupported() {
        return !!(typeof AudioContext === 'function'
            && AudioContext.prototype
            && 'setSinkId' in AudioContext.prototype
            && window.__safeMediaDevices
            && window.__safeMediaDevices.has('enumerateDevices'));
    }

    function attach(opts) {
        opts = opts || {};
        var $detect = el(opts.detect), $select = el(opts.select),
            $refresh = el(opts.refresh), $wrap = el(opts.wrap);
        var lang = COPY[opts.locale || window.__LOCALE || 'en'] || COPY.en;
        var S = opts.strings || {};
        var slug = opts.slug || (document.querySelector('meta[name=tool-slug]') || {}).content || '';
        var api = { supported: false, selectedId: function () { return selectedId; }, refresh: noop };
        var selectedId = '';

        function noop() {}
        function ae(f, extra) {
            try {
                if (window.__aeFeature) window.__aeFeature(Object.assign({ f: f, slug: slug }, extra || {}));
                else if (window.__ae) window.__ae('feature_use', Object.assign({ f: f, slug: slug }, extra || {}));
            } catch (e) {}
        }

        if (!isSupported()) {
            /* Hiding the wrapper is the honest outcome: on these engines the
               <select> could not route audio anywhere even if it were filled. */
            if ($wrap) hide($wrap); else { hide($detect); hide($select); hide($refresh); }
            ae('output_picker_unsupported');
            return api;
        }
        api.supported = true;
        if (!$select) return api;

        /* Inline note element — created lazily next to the control, so the
           three call sites need no extra markup and no extra CSS. */
        var $note = el(opts.note);
        function note(html, withGrant) {
            if (!$note) {
                $note = document.createElement('div');
                $note.className = 'aop-note';
                $note.style.cssText = 'flex-basis:100%;font-size:12px;line-height:1.5;color:var(--text-muted);' +
                    'margin-top:6px;text-align:start';
                ($select.parentNode || document.body).appendChild($note);
            }
            $note.innerHTML = html || '';
            if (withGrant) {
                var b = document.createElement('button');
                b.type = 'button';
                b.className = 'aop-grant';
                b.style.cssText = 'display:inline-block;margin-top:6px;padding:6px 12px;font:inherit;font-size:12px;' +
                    'font-weight:600;border-radius:var(--radius-sm);border:1px solid var(--accent);' +
                    'background:transparent;color:var(--accent);cursor:pointer';
                b.textContent = lang.grant;
                b.addEventListener('click', function () { grant(); });
                $note.appendChild(document.createElement('br'));
                $note.appendChild(b);
            }
            $note.style.display = html ? '' : 'none';
        }

        function populate(devices) {
            var outputs = devices.filter(function (d) { return d.kind === 'audiooutput'; });
            var prev = selectedId || $select.value;
            var seen = {}, added = 0;
            $select.innerHTML = '';
            var d0 = document.createElement('option');
            d0.value = '';
            d0.textContent = S['default'] || 'Default';
            $select.appendChild(d0);
            outputs.forEach(function (d, i) {
                if (!d.deviceId || /^default$/i.test(d.deviceId)) return;
                var label = d.label || ((S.unknown || 'Output') + ' ' + (i + 1));
                if (seen[label]) return;
                seen[label] = true;
                var o = document.createElement('option');
                o.value = d.deviceId;
                o.textContent = iconFor(label) + ' ' + label;
                if (d.deviceId === prev) o.selected = true;
                $select.appendChild(o);
                added++;
            });
            /* Reveal the <select> only when it can actually do something. An
               empty list must NOT latch the trigger off — that was defect 3. */
            if (added > 0) {
                hide($detect); show($select); show($refresh);
                note('');
            } else {
                show($detect);
                hide($select); hide($refresh);
            }
            if (typeof opts.onPopulate === 'function') { try { opts.onPopulate(added); } catch (e) {} }
            return added;
        }

        function apply(id) {
            selectedId = id;
            if (typeof opts.apply === 'function') { try { opts.apply(id); } catch (e) {} }
            ae('output_device_selected', { picked: id ? 1 : 0 });
        }

        function enumerate() {
            return navigator.mediaDevices.enumerateDevices();
        }

        /* Explicit, user-initiated microphone grant — the ONLY path that may
           touch getUserMedia here, and deliberately not routed through
           MicManager: its recovery modals are written for recording tools and
           its failures are reported as `tool_blocked`, which is wrong for a
           playback tool. Failure surfaces as an inline sentence. */
        function grant() {
            ae('output_labels_grant_click');
            var mdev = navigator.mediaDevices;
            if (!mdev || !mdev.getUserMedia) { note(lang.failed); return; }
            window.__tmbGum({ audio: true }).then(function (stream) {
                try { stream.getTracks().forEach(function (t) { t.stop(); }); } catch (e) {}
                return enumerate().then(function (devs) {
                    var n = populate(devs);
                    ae('output_labels_granted', { devices: n });
                });
            }).catch(function (err) {
                var name = err && err.name;
                note(name === 'NotFoundError' || name === 'DevicesNotFoundError' ? lang.nomic
                    : name === 'NotAllowedError' || name === 'SecurityError' ? lang.denied
                        : lang.failed);
                ae('output_labels_grant_failed', { err: String(name || 'unknown') });
            });
        }

        function detect(userInitiated) {
            return enumerate().then(function (devices) {
                var outputs = devices.filter(function (d) { return d.kind === 'audiooutput'; });
                var labelled = outputs.some(function (d) { return !!d.label; });
                if (labelled) { populate(devices); return; }
                if (!userInitiated) return;   // never nag on a background refresh
                /* No labels. Chromium gates output names behind a media
                   permission (measured: one placeholder entry with an empty
                   deviceId and empty label). Explain and let the user choose —
                   unless there is no microphone to grant in the first place. */
                var hasMicHardware = devices.some(function (d) { return d.kind === 'audioinput'; });
                if (!hasMicHardware) { note(lang.nomic); ae('output_labels_no_mic_hw'); return; }
                note(lang.why, true);
                ae('output_labels_prompt_shown');
            }).catch(function () { note(lang.failed); });
        }

        if ($detect) $detect.addEventListener('click', function () { detect(true); });
        if ($refresh) $refresh.addEventListener('click', function () { detect(true); });
        $select.addEventListener('change', function () { apply(this.value); });
        /* __onDeviceChange lives in timbrica-core.js. If that has not loaded for
           some reason, skip the listener rather than reintroduce the raw
           addEventListener call this module exists to avoid. */
        if (window.__onDeviceChange) {
            window.__onDeviceChange(function () {
                if ($select && !$select.classList.contains('hidden')) detect(false);
            });
        }

        /* Free win: if this origin already has the permission, fill the list on
           load so nobody has to press anything and nobody sees a prompt. */
        detect(false);

        api.refresh = function () { return detect(false); };
        return api;
    }

    window.AudioOutputPicker = { attach: attach, isSupported: isSupported, COPY: COPY };
})();
